import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button, Drawer, Input } from "antd";
import type { InputRef } from "antd";
import { BrandMark } from "@anyseal/ui";
import {
  DesktopOutlined,
  GlobalOutlined,
  MenuOutlined,
  MoonOutlined,
  SearchOutlined,
  SunOutlined,
} from "@ant-design/icons";
import { useI18n } from "../i18n";
import { useTheme, type ThemeMode } from "../useTheme";

const navLinks = {
  "zh-CN": [
    { key: "models",     path: "/models",         label: "模型" },
    { key: "enterprise", path: "/enterprise",     label: "企业方案" },
    { key: "docs",       path: "/dashboard/docs", label: "文档" },
  ],
  en: [
    { key: "models",     path: "/models",         label: "Models" },
    { key: "enterprise", path: "/enterprise",     label: "Enterprise" },
    { key: "docs",       path: "/dashboard/docs", label: "Docs" },
  ],
} as const;

/** Cycle order for the single theme-toggle button. */
const THEME_CYCLE: ThemeMode[] = ["light", "dark", "auto"];

const themeIcon: Record<ThemeMode, React.ReactNode> = {
  light: <SunOutlined />,
  dark: <MoonOutlined />,
  auto: <DesktopOutlined />,
};

const themeLabels = {
  "zh-CN": { light: "浅色", dark: "深色", auto: "跟随系统" },
  en: { light: "Light", dark: "Dark", auto: "System" },
} as const;

export function MarketingNav() {
  const { locale, setLocale } = useI18n();
  const { mode: themeMode, setMode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [query, setQuery] = useState("");
  const searchRef = useRef<InputRef | null>(null);

  const links = navLinks[locale];
  const activeKey =
    links.find((l) => location.pathname === l.path || location.pathname.startsWith(`${l.path}/`))?.key ?? "";

  const cycleTheme = () => {
    const idx = THEME_CYCLE.indexOf(themeMode);
    const next = THEME_CYCLE[(idx + 1) % THEME_CYCLE.length] ?? "light";
    setMode(next);
  };

  const toggleLocale = () => setLocale(locale === "zh-CN" ? "en" : "zh-CN");

  const themeLabel = themeLabels[locale][themeMode];
  const themeAria =
    locale === "zh-CN" ? `切换主题（当前：${themeLabel}）` : `Switch theme (current: ${themeLabel})`;

  /* ⌘K / Ctrl-K focuses the model search from anywhere on a marketing
     page. The input itself commits on Enter by navigating to /models. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const submitSearch = () => {
    // The catalog page owns filtering; the nav search is a jump point.
    navigate("/models");
  };

  return (
      <header className="mk-nav">
        <div className="mk-nav__inner">
          <Link
            className="mk-nav__brand"
            to="/"
            aria-label="LonMax AI"
          >
            <BrandMark size={28} />
            <span>LonMax AI</span>
          </Link>

          <nav
            className="mk-nav__links"
            aria-label={locale === "zh-CN" ? "主导航" : "Main navigation"}
          >
            {links.map((l) => (
              <Link
                key={l.key}
                to={l.path}
                className={`mk-nav__link${activeKey === l.key ? " mk-nav__link--active" : ""}`}
                aria-current={activeKey === l.key ? "page" : undefined}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="mk-nav__spacer" />

          <Input
            ref={searchRef}
            className="mk-nav__search"
            prefix={<SearchOutlined style={{ fontSize: 13 }} />}
            /* The ⌘K hint and the clear icon never coexist: the hint is
               idle-state affordance, the icon appears once there is text. */
            suffix={query ? undefined : <span className="mk-kbd" aria-hidden="true">⌘K</span>}
            placeholder={locale === "zh-CN" ? "搜索模型，如 gpt-4o" : "Search models, e.g. gpt-4o"}
            aria-label={locale === "zh-CN" ? "搜索模型" : "Search models"}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onPressEnter={submitSearch}
            allowClear
          />

          <div className="mk-nav__actions">
            <Button
              type="text"
              className="mk-nav__icon-btn"
              aria-label={themeAria}
              icon={themeIcon[themeMode]}
              onClick={cycleTheme}
            />

            <Button
              type="text"
              className="mk-nav__icon-btn"
              aria-label={locale === "zh-CN" ? "切换到英文" : "切换到中文"}
              title={locale === "zh-CN" ? "切换到英文" : "切换到中文"}
              icon={<GlobalOutlined />}
              onClick={toggleLocale}
            />

            <Link to="/login" className="mk-nav__signin">
              {locale === "zh-CN" ? "登录" : "Sign in"}
            </Link>
            <Link to="/register" className="mk-btn mk-btn--primary">
              {locale === "zh-CN" ? "注册" : "Sign up"}
            </Link>

            <Button
              type="text"
              className="mk-nav__hamburger"
              icon={<MenuOutlined />}
              aria-label={locale === "zh-CN" ? "打开菜单" : "Open menu"}
              onClick={() => setDrawerOpen(true)}
            />
          </div>
        </div>

        <Drawer
          open={drawerOpen}
          placement="right"
          size="default"
          onClose={() => setDrawerOpen(false)}
          closable
          title={
            <Link
              className="mk-nav__brand"
              to="/"
              onClick={() => setDrawerOpen(false)}
              aria-label="LonMax AI"
            >
              <BrandMark size={22} />
              <span>LonMax AI</span>
            </Link>
          }
        >
          <nav className="mk-drawer-nav">
            {links.map((l) => (
              <Link
                key={l.key}
                to={l.path}
                className="mk-drawer-nav__link"
                onClick={() => setDrawerOpen(false)}
              >
                {l.label}
              </Link>
            ))}
            <div className="mk-drawer-nav__divider" />
            <Link to="/login" className="mk-drawer-nav__link" onClick={() => setDrawerOpen(false)}>
              {locale === "zh-CN" ? "登录" : "Sign in"}
            </Link>
            <Link
              to="/register"
              className="mk-btn mk-btn--primary mk-drawer-nav__cta"
              onClick={() => setDrawerOpen(false)}
            >
              {locale === "zh-CN" ? "注册" : "Sign up"}
            </Link>
          </nav>
        </Drawer>
      </header>
  );
}
